import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
// import categories from "../../utils/categories";
import logo from "../../assets/digikorestudios.png";
import { Pause, Play, MoreVertical, Copy } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getSongsByCategory } from "../../redux/reduxSlices/manageCategorySlice";

const SongsPage = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [subCategories, setSubCategories] = useState([]);
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState(null);
  const [currentPlayingSubCategory, setCurrentPlayingSubCategory] =
    useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [progress, setProgress] = useState(0); // 0–100
  const [currentTime, setCurrentTime] = useState(0); // seconds
  const [duration, setDuration] = useState(0); // seconds

  const [imagePath, setImagePath] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);

  const { loading, success, getSongsByCategoryResponse } = useSelector(
    (state) => state.manageCategory,
  );

  useEffect(() => {
    dispatch(getSongsByCategory(categoryName));
  }, [categoryName]);

  const audioRef = useRef(new Audio());

  // prevents timeupdate from fighting with the slider while dragging
  const isSeekingRef = useRef(false);

  // 🔹 Load songs for the selected category
  useEffect(() => {
    const currentCategory = getSongsByCategoryResponse?.data;
    // const currentCategory = categories.find(
    //   (cat) => cat.slug.toLowerCase() === categoryName.toLowerCase(),
    // );

    if (currentCategory) setImagePath(currentCategory.image);

    if (currentCategory?.songs) {
      setSubCategories(currentCategory.songs);
    }
  }, [getSongsByCategoryResponse]);

  // 🔹 Update progress & cleanup on unmount
  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (!audio) return;

      // While user is dragging slider, don't override the UI with timeupdate
      if (isSeekingRef.current) return;

      const dur = audio.duration || 0;
      const ct = audio.currentTime || 0;

      setDuration(dur);
      setCurrentTime(ct);

      if (dur > 0) {
        setProgress((ct / dur) * 100);
      } else {
        setProgress(0);
      }
    };

    const handleEnded = () => {
      setCurrentlyPlayingIndex(null);
      setCurrentPlayingSubCategory(null);
      setProgress(0);
      setCurrentTime(0);
      setIsPlaying(false);
    };

    const handleLoadedMeta = () => {
      setDuration(audio.duration || 0);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", handleLoadedMeta);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("loadedmetadata", handleLoadedMeta);

      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  // 🔹 Handle play/pause
  const handlePlayPause = (subCategoryIndex, songIndex, song) => {
    const audio = audioRef.current;

    // Create a unique identifier for the current song
    const currentSongId = `${subCategoryIndex}-${songIndex}`;
    const playingSongId =
      currentlyPlayingIndex !== null
        ? `${currentPlayingSubCategory}-${currentlyPlayingIndex}`
        : null;

    // ✅ Same song toggled
    if (playingSongId === currentSongId) {
      if (audio.paused) {
        audio
          .play()
          .then(() => setIsPlaying(true))
          .catch((err) => console.warn("Resume failed:", err));
      } else {
        audio.pause();
        setIsPlaying(false);
      }
      return;
    }

    // 🎵 New song selected
    audio.pause();
    audio.currentTime = 0;

    audio.src = song.url;

    audio
      .play()
      .then(() => {
        setCurrentlyPlayingIndex(songIndex);
        setCurrentPlayingSubCategory(subCategoryIndex);
        setIsPlaying(true);
      })
      .catch((err) => console.warn("Playback failed:", err));
  };

  // 🔹 Handle Back button
  const handleBack = () => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setCurrentlyPlayingIndex(null);
    setCurrentPlayingSubCategory(null);
    setIsPlaying(false);
    navigate("/jingle-collection");
  };

  const closePlayer = () => {
    const audio = audioRef.current;

    audio.pause();
    audio.currentTime = 0;

    setIsPlaying(false);
    setCurrentlyPlayingIndex(null);
    setCurrentPlayingSubCategory(null);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
  };

  // 🔹 Format time helper
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined || isNaN(seconds))
      return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ✅ SEEK handlers (drag slider)
  const seekToPercent = (percent) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || isNaN(audio.duration)) return;

    const nextTime = (percent / 100) * audio.duration;
    audio.currentTime = nextTime;

    // update UI instantly
    setProgress(percent);
    setCurrentTime(nextTime);
  };

  const handleSeekChange = (e) => {
    const value = Number(e.target.value); // 0–100
    seekToPercent(value);
  };

  const handleSeekStart = () => {
    isSeekingRef.current = true;
  };

  const handleSeekEnd = (e) => {
    const value = Number(e.target.value);
    seekToPercent(value);
    isSeekingRef.current = false;
  };

  // Get current song title for display
  const getCurrentSongTitle = () => {
    if (currentPlayingSubCategory !== null && subCategories.length > 0) {
      const subCategory = subCategories[currentPlayingSubCategory];
      if (
        subCategory &&
        subCategory.songList &&
        subCategory.songList[currentlyPlayingIndex]
      ) {
        return `Sample ${currentlyPlayingIndex + 1}`;
      }
    }
    return "Unknown Song";
  };

  // Get current subcategory name
  const getCurrentSubCategoryName = () => {
    if (
      currentPlayingSubCategory !== null &&
      subCategories[currentPlayingSubCategory]
    ) {
      return subCategories[currentPlayingSubCategory].subCategory;
    }
    return categoryName;
  };

  // Calculate total number of songs
  const getTotalSongs = () => {
    return subCategories.reduce(
      (total, sub) => total + (sub.songList?.length || 0),
      0,
    );
  };

  // ---- DevTools redirect logic (kept as-is) ----
  useEffect(() => {
    const threshold = 160;

    const isMobile = () =>
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(
        navigator.userAgent,
      ) || window.innerWidth < 768;

    const redirectAway = () => {
      const audio = audioRef.current;
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setCurrentlyPlayingIndex(null);
      setCurrentPlayingSubCategory(null);
      navigate("/jingle-collection", { replace: true });
    };

    const sizeGapOpen = () =>
      window.outerWidth - window.innerWidth > threshold ||
      window.outerHeight - window.innerHeight > threshold;

    const timingOpen = () => {
      const start = performance.now();
      debugger; // eslint-disable-line no-debugger
      const end = performance.now();
      return end - start > 50;
    };

    const detectDevTools = () => {
      if (isMobile()) return;
      if (sizeGapOpen() || timingOpen()) redirectAway();
    };

    detectDevTools();
    const t = setTimeout(detectDevTools, 300);

    window.addEventListener("resize", detectDevTools);
    const interval = setInterval(detectDevTools, 800);

    return () => {
      clearTimeout(t);
      clearInterval(interval);
      window.removeEventListener("resize", detectDevTools);
    };
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuIndex !== null) {
        // Check if click is on burger menu button or dropdown
        const burgerButton = event.target.closest(".burger-menu-button");
        const dropdown = event.target.closest(".dropdown-menu");

        if (!burgerButton && !dropdown) {
          setOpenMenuIndex(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuIndex]);

  const capitalizeEachWord = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50 via-white to-amber-50 relative overflow-hidden">
      <div className="flex-1">
        {/* Header */}
        <div className="container mx-auto px-4 py-8 relative z-10">
          <div className="w-full flex items-center justify-center mb-12 mt-10 max-md:mb-4">
            <div className="text-center flex flex-col justify-center items-center">
              <img
                src={logo}
                className="w-23 h-14 mb-4"
                alt="Digikore Studios"
              />
              <h1 className="text-4xl max-md:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2 capitalize">
                {capitalizeEachWord(categoryName)} Collection
              </h1>
              <p className="text-gray-600 text-lg">
                {getTotalSongs()} jingles to explore
              </p>
            </div>
          </div>

          {/* Songs Grid - Grouped by SubCategory */}
          <div className="space-y-12">
            {subCategories.map((subCategory, subCatIndex) => (
              <div key={subCatIndex} className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b-2 border-orange-400 inline-block">
                  {capitalizeEachWord(subCategory.subCategory)}
                </h2>
                <div className="grid max-md:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-8">
                  {subCategory.songList &&
                    subCategory.songList.map((song, songIndex) => (
                      <div
                        key={song.id}
                        className="group relative cursor-pointer transform transition-all duration-500 hover:scale-105 "
                      >
                        <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 group-hover:shadow-2xl transition-all duration-300">
                          <img
                            src={imagePath}
                            alt={`Sample ${songIndex + 1}`}
                            className="absolute inset-0 w-full object-cover filter group-hover:brightness-110 transition-all duration-300 -z-20 rounded-2xl h-full"
                          />
                          {/* Burger Menu Button */}
                          <div className="absolute top-2 right-2 z-10">
                            <button
                              className="burger-menu-button bg-black/50 backdrop-blur-sm p-2 rounded-full hover:bg-black/70 transition-all duration-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuIndex(
                                  openMenuIndex === song.id ? null : song.id,
                                );
                              }}
                            >
                              <MoreVertical size={18} color="white" />
                            </button>

                            {/* Dropdown Menu */}
                            {openMenuIndex === song.id && (
                              <div className="dropdown-menu absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl z-20 overflow-hidden">
                                <button
                                  onClick={() => {
                                    const songUrl = `${window.location.origin}/jingle-collection/${categoryName}/${subCategory.subCategory.toLowerCase().replace(/ /g, "-")}/${songIndex}`;
                                    navigator.clipboard.writeText(songUrl);
                                    setOpenMenuIndex(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-300 flex items-center gap-2"
                                >
                                  <Copy size={14} />
                                  Copy URL
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="text-center flex justify-center items-center space-y-3">
                            <button
                              onClick={() =>
                                handlePlayPause(subCatIndex, songIndex, song)
                              }
                              className={` max-md:w-[50px] p-2 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                                currentlyPlayingIndex === songIndex &&
                                currentPlayingSubCategory === subCatIndex &&
                                isPlaying
                                  ? "bg-gradient-to-r from-orange-500 to-amber-500 shadow-lg text-white"
                                  : currentlyPlayingIndex === songIndex &&
                                      currentPlayingSubCategory === subCatIndex
                                    ? "bg-yellow-500 text-white"
                                    : "bg-orange-200 text-gray-700 hover:bg-orange-300"
                              }`}
                            >
                              {currentlyPlayingIndex === songIndex &&
                              currentPlayingSubCategory === subCatIndex &&
                              isPlaying ? (
                                <>
                                  <Pause size={16} />
                                  {/* <span>Pause</span> */}
                                </>
                              ) : (
                                <>
                                  <Play size={16} />
                                  {/* <span>Play</span> */}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        <h3 className="text-l max-md:text-s font-bold text-black drop-shadow-2xl  transition-all duration-300 tracking-tight mb-4 px-3 relative">
                          {/* Sample {songIndex + 1} */}
                          {capitalizeEachWord(song?.genre || `Sample ${songIndex + 1}`)}
                        </h3>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Player */}
        {currentlyPlayingIndex !== null && (
          <div className="fixed bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-6 z-[90]">
            {/* Desktop layout (hidden on mobile) */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                  🎵
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-lg">
                    {getCurrentSongTitle()}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {getCurrentSubCategoryName()}
                  </p>
                </div>
              </div>

              {/* Desktop seek bar */}
              <div className="flex-1 max-w-md mx-6">
                <div className="w-full">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.1"
                    value={progress}
                    onMouseDown={handleSeekStart}
                    onTouchStart={handleSeekStart}
                    onChange={handleSeekChange}
                    onMouseUp={handleSeekEnd}
                    onTouchEnd={handleSeekEnd}
                    className="w-full h-2 rounded-full cursor-pointer accent-orange-500"
                  />
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    const subCategory =
                      subCategories[currentPlayingSubCategory];
                    if (
                      subCategory &&
                      subCategory.songList[currentlyPlayingIndex]
                    ) {
                      handlePlayPause(
                        currentPlayingSubCategory,
                        currentlyPlayingIndex,
                        subCategory.songList[currentlyPlayingIndex],
                      );
                    }
                  }}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold shadow-lg hover:scale-105 transition-all duration-300"
                >
                  {isPlaying ? "Pause" : "Play"}
                </button>

                <button
                  onClick={closePlayer}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Mobile layout (visible on mobile) */}
            <div className="md:hidden">
              {/* Top row: Song info and controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                    🎵
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-800 truncate">
                      {getCurrentSongTitle()}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {getCurrentSubCategoryName()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      const subCategory =
                        subCategories[currentPlayingSubCategory];
                      if (
                        subCategory &&
                        subCategory.songList[currentlyPlayingIndex]
                      ) {
                        handlePlayPause(
                          currentPlayingSubCategory,
                          currentlyPlayingIndex,
                          subCategory.songList[currentlyPlayingIndex],
                        );
                      }
                    }}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold shadow-lg hover:scale-105 transition-all duration-300 text-sm"
                  >
                    {isPlaying ? "Pause" : "Play"}
                  </button>
                  <button
                    onClick={closePlayer}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Mobile seek bar - full width and larger */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1 text-xs text-gray-600">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={progress}
                  onMouseDown={handleSeekStart}
                  onTouchStart={handleSeekStart}
                  onChange={handleSeekChange}
                  onMouseUp={handleSeekEnd}
                  onTouchEnd={handleSeekEnd}
                  className="w-full h-3 rounded-full cursor-pointer accent-orange-500"
                />
              </div>
            </div>
          </div>
        )}

        <div className="w-full flex justify-center items-center mt-8 mb-4">
          <button
            onClick={handleBack}
            className="group flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-200 hover:scale-105"
          >
            <span className="text-orange-600 font-semibold group-hover:text-orange-700 transition-colors">
              View All Categories
            </span>
          </button>
        </div>

        {/* Copyright Notice */}
        <div className="mb-8 animate-slide-up delay-200">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg p-4 max-w-4xl mx-auto shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div className="text-left">
                <p className="text-amber-800 font-medium mb-1">
                  Important Notice
                </p>
                <p className="text-amber-700 text-sm">
                  All sample jingles are provided solely for reference purposes.
                  We do not copy, reproduce, or imitate any existing jingles.
                  Each jingle presented is an original work with its own unique
                  composition and lyrics, and all jingles are protected by their
                  respective copyrights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="w-full bg-gray-100 text-center py-4 text-sm text-gray-600">
        <p>
          For contact and support:{" "}
          <a
            href="mailto:prapti@digikore.com"
            className="text-blue-600 hover:underline"
          >
            prapti@digikore.com
          </a>
        </p>
        <p className="mt-1">© 2026 Digikore Studios Limited</p>
      </footer>
    </div>
  );
};

export default SongsPage;
